// Filter shortcuts — navigate to WAF Events list with pre-applied pills.

import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import {
  Activity,
  ArrowRight,
  Crosshair,
  Globe,
  Link2,
  MapPin,
  Server,
  ShieldBan,
} from "lucide-react";

import { formatBurstTooltip } from "../../../../../utils/wafEventV3";
import colors from "../../../../../utils/colors";
import { DETECTION_ICONS } from "../../../../../icons/detectionLucideIcons";
import { getWafEventStatus } from "../wafEventListUtils";
import { PRIMARY_COLOR, TEXT_SECONDARY, primaryButtonStyle } from "../wafEventDrawerShared";
import {
  SmartParseSectionCard,
  SP,
  spIconLabelGap,
  spIconBoxForColor,
  spBurstCalloutSx,
  spBurstIconBoxSx,
  spSimilarFilterCardSx,
  spSimilarFilterLabelSx,
  spSimilarFilterValueSx,
  spSecondaryBodySx,
  spLh,
} from "./smartParseDrawerUi";

function SimilarFilterCard({ label, value, icon: Icon, onClick }) {
  return (
    <Box onClick={onClick} sx={spSimilarFilterCardSx}>
      <Stack direction="row" alignItems="center" spacing={0} sx={{ gap: spIconLabelGap }}>
        <Box sx={spIconBoxForColor(PRIMARY_COLOR, "section")}>
          <Icon size={16} color={PRIMARY_COLOR} />
        </Box>
        <Typography component="span" sx={{ flex: 1, minWidth: 0 }}>
          <Box component="span" sx={spSimilarFilterLabelSx}>
            {label}
            {" "}
          </Box>
          <Box component="span" sx={spSimilarFilterValueSx}>
            {value}
          </Box>
        </Typography>
      </Stack>
    </Box>
  );
}

export default function ViewSimilarEventsPanel({ event, onFilterSimilar }) {
  if (!event) return null;

  const sourceIp = event.attacker?.source_ip || event.attacker?.ip;
  const countryIso = event.attacker?.geo?.country_iso;
  const hostname = event.target?.host;
  const uri = event.target?.uri || event.target?.path;
  const attackType = event.primary_intent?.intent_name
    || (Array.isArray(event.attack_types) ? event.attack_types[0] : null);
  const status = event.similarStatus || getWafEventStatus({ action: event.assessment?.action });

  const correlation = event.correlation;
  const burstCount = Number(correlation?.related_event_count);
  const hasBurst = Number.isFinite(burstCount) && burstCount >= 2;
  const burstTooltip = hasBurst ? formatBurstTooltip(correlation) : null;

  const filterCards = [
    sourceIp && { key: "ip", label: "IP Address", value: sourceIp, icon: Globe },
    attackType && { key: "attack_type", label: "Attack Type", value: attackType, icon: Crosshair },
    hostname && { key: "hostname", label: "Hostname", value: hostname, icon: Server },
    uri && { key: "uri", label: "URI Path", value: uri, icon: Link2 },
    countryIso && { key: "country", label: "Country", value: countryIso, icon: MapPin },
    status && { key: "status", label: "Status", value: status, icon: ShieldBan },
  ].filter(Boolean);

  const hasAnyFilter = filterCards.length > 0 || hasBurst;
  if (!hasAnyFilter) return null;

  return (
    <SmartParseSectionCard icon={DETECTION_ICONS.view} title="View Similar Events" defaultExpanded={false}>
      <Typography sx={{ ...spSecondaryBodySx, mb: 2 }}>
        Filter events to view a campaign or similar attack patterns based on this event&apos;s characteristics.
      </Typography>

      {hasBurst && (
        <Stack direction="row" alignItems="center" sx={{ ...spBurstCalloutSx, mb: filterCards.length > 0 ? 2 : 0 }}>
          <Box sx={spBurstIconBoxSx}>
            <Activity size={18} color={colors.red[200]} />
          </Box>
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: spLh.px21, color: colors.red[200] }}>
              Part of a burst — {burstCount} related events
            </Typography>
            {burstTooltip && (
              <Typography sx={{ fontSize: 12, lineHeight: spLh.px18, color: TEXT_SECONDARY }}>
                {burstTooltip}
              </Typography>
            )}
          </Stack>
          <Button
            variant="contained"
            endIcon={<ArrowRight size={16} />}
            onClick={() => onFilterSimilar?.("burst")}
            sx={{ ...primaryButtonStyle, flexShrink: 0 }}
          >
            View related
          </Button>
        </Stack>
      )}

      {filterCards.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: `${SP.gridCellX}px`,
          }}
        >
          {filterCards.map(({ key, label, value, icon }) => (
            <SimilarFilterCard
              key={key}
              label={label}
              value={value}
              icon={icon}
              onClick={() => onFilterSimilar?.(key)}
            />
          ))}
        </Box>
      )}
    </SmartParseSectionCard>
  );
}
